// frontend/src/components/consent/PrivacyPermissionsSection.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { getMyPrivacySettings } from "../../lib/legal/consent";

function formatDate(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function PrivacyPermissionsSection() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState({ purposes: [], documents: [] });
  const [error, setError] = useState("");

  const loadSettings = useCallback(async ({ background = false } = {}) => {
    try {
      if (background) setRefreshing(true);
      else setLoading(true);
      setError("");
      const result = await getMyPrivacySettings();
      setSettings({
        purposes: result?.purposes || [],
        documents: result?.documents || [],
      });
    } catch (loadError) {
      console.error("Could not load privacy settings:", loadError);
      setError(loadError?.message || "Could not load your privacy settings.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const requiredPurposes = useMemo(
    () => settings.purposes.filter((purpose) => purpose.permission_type === "required"),
    [settings.purposes]
  );

  return (
    <section className="mt-6 rounded-3xl border border-[#EFEFEF] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF4EC] text-[#FF914D]">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#333333]">Privacy and consent</h2>
            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
              Review the information Gwin processes and the legal documents associated with your account.
            </p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Refresh privacy settings"
          disabled={refreshing || loading}
          onClick={() => loadSettings({ background: true })}
          className="rounded-full border border-[#E5E5E5] p-2 text-[#757575] hover:bg-[#FAFAFA] disabled:opacity-60"
        >
          <RefreshCw className={["h-4 w-4", refreshing ? "animate-spin" : ""].join(" ")} />
        </button>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center justify-center rounded-2xl bg-[#FAFAFA] px-4 py-5 text-sm font-semibold text-[#757575]">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading privacy settings…
        </div>
      ) : (
        <>
          {error && (
            <div role="alert" className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
              {error}
            </div>
          )}

          <div className="mt-5">
            <h3 className="text-sm font-bold text-[#333333]">Required processing</h3>
            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
              These activities are needed to provide, secure and operate your Gwin account.
              They cannot be disabled while your account remains active.
            </p>
            <div className="mt-3 space-y-3">
              {requiredPurposes.map((purpose) => (
                <div key={purpose.purpose_key} className="rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-[#333333]">{purpose.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-[#757575]">{purpose.description}</p>
                    </div>
                    <span className="inline-flex shrink-0 items-center rounded-full bg-green-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-green-700">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Required
                    </span>
                  </div>
                  {purpose.legal_basis && (
                    <p className="mt-2 text-[11px] font-semibold text-[#999999]">Basis: {purpose.legal_basis}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-bold text-[#333333]">Legal documents</h3>
            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
              Review the current versions of the documents connected to your account.
            </p>
            <div className="mt-3 space-y-2">
              {settings.documents.map((document) => (
                <Link
                  key={document.document_id}
                  to={document.route_path || "/privacy"}
                  state={{ backTo: "/profile", backLabel: "Back to profile", replaceBack: true }}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 transition hover:border-[#FF914D]/50 hover:bg-[#FFF9F5]"
                >
                  <div>
                    <p className="text-sm font-bold text-[#333333]">{document.title}</p>
                    <p className="mt-1 text-[11px] text-[#757575]">
                      Version {document.version || document.revision || "current"} {" • "}
                      Acknowledged {formatDate(document.acknowledged_at)}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 shrink-0 text-[#FF914D]" />
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
